import {
	BaseQueryApi,
	BaseQueryFn,
	FetchArgs,
	fetchBaseQuery,
	FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query/react";
import MAIN_PATHS from "src/business.InterfaceLayer/store/mainPaths";
import HTTP_STATUSES from "src/general/constants/http/httpStatuses";
import COMMON_FIELDS from "src/general/constants/commonFields";
import { AUTH_FIELDS } from "../../../entities/auth.entity/redux/fields";
import AUTH_PATHS from "../../../entities/auth.entity/redux/paths";
import { IAuth } from "../../../entities/auth.entity/redux/type";
import HTTP_METHODS from "../../../../../general/constants/http/httpMethods";
import { authActions } from "../../../entities/auth.entity/redux/slice";
import {
	ERROR_HANDLER_SERVICE_TYPE,
	errorHandlerService,
} from "../../../../../general/utils/error-handler.service";
import OtherRoutesPaths from "../../../../../user.InterfaceLayer/router/routes/other.route/other.routesPaths";
import errorLocaleService from "../errorLocale.service";

type AuthApiState = {
	[containerKey: string]: IAuth;
};

type IBaseQueryWithReAuth = BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
>;

const getTokenFromApi = (
	api: Pick<BaseQueryApi, "getState">,
	containerKey = MAIN_PATHS.AUTHORIZATION
) => {
	const state = api.getState();

	if (state && typeof state === "object" && isApiState(state, containerKey)) {
		return state[`${containerKey}Container`][AUTH_FIELDS.TOKEN_ACCESS];
	}
};

const getRefreshTokenFromApi = (
	api: Pick<BaseQueryApi, "getState">,
	containerKey = MAIN_PATHS.AUTHORIZATION
) => {
	const state = api.getState();

	if (state && typeof state === "object" && isApiState(state, containerKey)) {
		return state[`${containerKey}Container`][AUTH_FIELDS.TOKEN_REFRESH];
	}
};

const isApiState = (
	data: object,
	containerKey = MAIN_PATHS.AUTHORIZATION
): data is AuthApiState => {
	return `${containerKey}Container` in data;
};

const baseQuery = fetchBaseQuery({
	baseUrl: window.location.origin + "/api",
	prepareHeaders: (headers, api) => {
		const token = getTokenFromApi(api);
		token && headers.set("token", `${token}`);
		headers.set("Access-Control-Allow-Origin", "*");
		headers.set("Accept", "application/json");
		headers.set(
			"Access-Control-Allow-Headers",
			"Origin, X-Requested-With, Content-Type, Accept"
		);
		headers.set(
			"Access-Control-Allow-Methods",
			"DELETE, PUT, UPDATE, HEAD, OPTIONS, GET, POST"
		);

		return headers;
	},
});

const argumentsRefreshToken = (args: string | FetchArgs, api: BaseQueryApi) => {
	const newArg = JSON.parse(JSON.stringify(args));
	newArg.url = `/${MAIN_PATHS.AUTHORIZATION}${AUTH_PATHS.TOKEN_REFRESH}`;
	newArg.method = HTTP_METHODS.POST;
	newArg.body = {
		[AUTH_FIELDS.TOKEN_REFRESH]: getRefreshTokenFromApi(api),
	};
	return newArg;
};

const argumentsPing = (args: string | FetchArgs, api: BaseQueryApi) => {
	const newArg = JSON.parse(JSON.stringify(args));
	newArg.url = `/${MAIN_PATHS.AUTHORIZATION}${AUTH_PATHS.GET_ME}`;
	newArg.method = HTTP_METHODS.POST;
	newArg.body = {
		[AUTH_FIELDS.TOKEN_REFRESH]: getRefreshTokenFromApi(api),
	};
	return newArg;
};

const baseQueryWithReAuth: IBaseQueryWithReAuth = async (
	args,
	api,
	extraOptions
): Promise<any> => {
	let result = await baseQuery(args, api, extraOptions);

	// TODO: костыль бека (запрос на авторизацию выдает 401)
	if (
		result?.meta?.request?.url !==
		`${window.location.origin}/${MAIN_PATHS.USER}${AUTH_PATHS.LOGIN}`
	) {
		// Если токен мертвый тогда рефрешим его
		if (result?.meta?.response?.status === HTTP_STATUSES.UNAUTHORIZED) {
			const refreshResult = await baseQuery(
				argumentsRefreshToken(args, api),
				api,
				extraOptions
			);
			if (refreshResult.data) {
				// @ts-ignore
				const data = refreshResult?.data?.data;
				api.dispatch(
					authActions.login({
						[AUTH_FIELDS.TOKEN_ACCESS]: data?.token,
						[AUTH_FIELDS.TOKEN_REFRESH]: data?.refresh_token,
						[AUTH_FIELDS.IS_NEED_CHANGE]: data?.isNeedChange ? "true" : "false",
						[AUTH_FIELDS.ROLE]: data?.owner?.roles,
						[AUTH_FIELDS.USER_ID]: data?.owner?.id || null,
					})
				);
				result = await baseQuery(args, api, extraOptions);
				if (!result?.meta?.response?.ok) {
					api.dispatch(authActions.logout());
				}
			} else {
				api.dispatch(authActions.logout());
			}
		}
	}

	// Проверка на то что запрос не с Ping запроса
	if (
		result?.meta?.request?.url !==
		`${window.location.origin}/${MAIN_PATHS.AUTHORIZATION}${AUTH_PATHS.PING}`
	) {
		if (
			result?.meta?.response?.status === HTTP_STATUSES.INTERNAL_SERVER_ERROR ||
			result?.meta?.response?.status === HTTP_STATUSES.CORS
		) {
			const pingResult = await baseQuery(
				argumentsPing(args, api),
				api,
				extraOptions
			);
			// @ts-ignore
			const status = pingResult?.error?.originalStatus;
			if (
				status === HTTP_STATUSES.INTERNAL_SERVER_ERROR ||
				result?.meta?.response?.status === HTTP_STATUSES.CORS
			) {
				window.location.href = OtherRoutesPaths.INTERNAL_SERVER_ERROR;
			}
		}
	}

	if (!result?.meta?.response?.ok) {
		const err = result?.error?.data;
		// console.log(
		// 	"исходные ошибки сервера err baseQuery in createApi redux",
		// 	err
		// );

		// @ts-ignore
		const messageValidationError = err?.validationError?.body[0]?.message;
		// @ts-ignore
		const codeValidationError = err?.validationError?.body[0]?.name;
		// @ts-ignore
		const detailError = err?.detail;

		const metaErr = errorLocaleService(
			result?.meta?.response?.statusText,
			result?.meta?.response?.status
		);
		// @ts-ignore
		const text = messageValidationError || err?.message || metaErr;

		errorHandlerService({
			[COMMON_FIELDS.STATUS]: result?.meta?.response?.status || 102,
			[COMMON_FIELDS.DATA]: `url:${result?.meta?.request?.url}
			 method:${result?.meta?.request?.method}
			 status:${result?.meta?.response?.status || 102}
			 err:${text}
			 body:${JSON.stringify(result?.meta?.response?.body || "-").slice(0, 90)}`,
			[COMMON_FIELDS.TYPE]: ERROR_HANDLER_SERVICE_TYPE.FULL_MODE,
		});

		return {
			[COMMON_FIELDS.ERROR]: {
				[COMMON_FIELDS.STATUS]: result?.meta?.response?.status || 102,
				[COMMON_FIELDS.DATA]: text,
				[COMMON_FIELDS.CODE]: codeValidationError,
				[COMMON_FIELDS.DETAIL]: detailError,
			},
		};
	}

	if (result?.error?.status === "PARSING_ERROR") {
		return { data: {}, meta: result?.meta };
	}
	return result;
};

export default baseQueryWithReAuth;
